import { formatISO } from 'date-fns'
import nid from 'nid'
import { db } from '../store/db'
import { collapseText, formatDateTime, log } from '../common/utils'
import {
  Message,
  MessageToTypes,
  StandardMessages,
  Walk,
} from '../store/dbTypes'
import { sendPushNotificationsForMessage } from '../PushNotifications/pushNotifications'

export const addMessage = async (message: Message): Promise<void> => {
  await db.messages.add(message)
}

export const updateMessage = async (message: Message): Promise<void> => {
  await db.messages.update(message.messageId, {
    ...message,
    updatedAt: formatISO(new Date()),
  })
}

export const upsertMessage = async (message: Message): Promise<void> => {
  const result = await db.messages.update(message.messageId, {
    ...message,
    updatedAt: formatISO(new Date()),
  })
  if (!result) await addMessage(message)
}

export const deleteMessage = async (id?: string): Promise<void> => {
  if (!id) return
  await db.messages.delete(id)
}

export const replaceMessagesTable = async (
  newMessages: Message[]
): Promise<void> => {
  try {
    const allMessages = await db.messages.toArray()
    const allMessageIds = allMessages.map((u) => u.messageId)
    await db.messages.bulkDelete(allMessageIds)
    await db.messages.bulkPut(newMessages)
  } catch (e: any) {
    log(`Error while replacing messages table: ${e.message}`)
  }
}

export const sendStandardMessage = async (
  type: StandardMessages,
  id?: string,
  userName?: string
) => {
  if (!id) return

  const currentAppStatus = await db.appStatus.get('Tripsheets')
  const usersDexie = await db.users.toArray()
  if (!currentAppStatus || !usersDexie.length) return

  const msgNotWalkSpecific = ['visitorJoined', 'memberPaid'].includes(type)
  const membershipRelated = ['visitorJoined', 'memberPaid'].includes(type)
  const forLeaderOnly = [
    'registeredForWalk',
    'visitorAfterGrace',
    'visitorMultiDay',
  ].includes(type)
  const forPrimaryContacts = [
    'primaryContactAssigned',
    'primaryContactStarted',
  ].includes(type)

  const walk = await db.walks.get(id)
  if (!msgNotWalkSpecific && !walk) return

  // Define the standard text
  let messageText = ''
  if (type === 'walkEnded') {
    messageText = `Walk ended "${walk?.title}" at ${formatDateTime(
      formatISO(new Date())
    )}`
  } else if (type === 'walkCancelled') {
    messageText = `Walk cancelled "${walk?.title}" at ${formatDateTime(
      formatISO(new Date())
    )}`
  } else if (type === 'registeredForWalk') {
    messageText = `${userName} has registered for walk "${walk?.title}"`
  } else if (type === 'visitorAfterGrace') {
    messageText = `Visitor ${userName} was prevented from registering for walk "${walk?.title}" due to too many walks as a visitor`
  } else if (type === 'visitorMultiDay') {
    messageText = `Visitor ${userName} was prevented from registering for walk "${walk?.title}" as this is a camp or multi-day walk`
  } else if (type === 'visitorJoined') {
    messageText = `${userName} has joined the club, but not yet paid`
  } else if (type === 'memberPaid') {
    messageText = `${userName} has paid their membership fee`
  } else if (type === 'primaryContactAssigned') {
    messageText = `You have been assigned as the primary contact for walk "${
      walk?.title
    }" on ${formatDateTime(walk?.date, 'dd-MM-yyyy')}. The expected ${
      walk?.dateTo
        ? 'end date is ' + formatDateTime(walk.dateTo, 'dd-MM-yyyy') + ' and '
        : ''
    } end time is ${formatDateTime(walk?.endTime, 'h:mmaaa')}`
  } else if (type === 'primaryContactStarted') {
    messageText = `The walk "${
      walk?.title
    }" where you are Primary Contact has started. The expected ${
      walk?.dateTo
        ? 'end date is ' + formatDateTime(walk.dateTo, 'dd-MM-yyyy') + ' and '
        : ''
    } end time is ${formatDateTime(walk?.endTime, 'h:mmaaa')}`
  } else {
    return
  }

  // Template message object
  const message: Message = {
    messageId: nid(),
    createdAt: formatISO(new Date()),
    updatedAt: formatISO(new Date()),
    sentAt: formatISO(new Date()),
    status: 'sent',
    fromUserId: currentAppStatus.userId,
    to: 'walk',
    toId: id,
    message: messageText,
  }

  // Specific walk-related messages
  if (!msgNotWalkSpecific && walk) {
    // Messages for the leader only
    if (forLeaderOnly) {
      const leaderIds =
        typeof walk.leaderId === 'string' ? [walk.leaderId] : walk.leaderId
      for (const leaderId of leaderIds || []) {
        const messageToLeader = {
          ...message,
          messageId: nid(),
          to: 'user' as MessageToTypes,
          toId: leaderId,
        }
        await addMessage(messageToLeader)
        await sendPushNotificationsForMessage(messageToLeader)
      }
    } else if (forPrimaryContacts) {
      if (walk.primaryContactIds && walk.primaryContactIds.length) {
        // Messages for the primary contact
        const messageToPrimaryContact = {
          ...message,
          messageId: nid(),
          to: 'user' as MessageToTypes,
          toId: walk.primaryContactIds[0],
        }
        await addMessage(messageToPrimaryContact)
        await sendPushNotificationsForMessage(messageToPrimaryContact)
      }
    } else {
      // The msg to the walkers in db and push
      await addMessage(message)
      await sendPushNotificationsForMessage(message)

      // The msg to admins(=leaders) for all msgs
      const admins = usersDexie.filter((u) => u.isAdmin || u.isSuperAdmin)
      for (const user of admins) {
        const updatedMessage = {
          ...message,
          messageId: nid(),
          to: 'user' as MessageToTypes,
          toId: user.userId,
        }
        await addMessage(updatedMessage)
        await sendPushNotificationsForMessage(updatedMessage)
      }
    }
  }

  // Specific membership-related messages
  if (membershipRelated) {
    const membershipOfficers = usersDexie.filter(
      (u) => u.isMembership || u.isSuperAdmin
    )
    for (const user of membershipOfficers) {
      const updatedMessage = {
        ...message,
        messageId: nid(),
        to: 'user' as MessageToTypes,
        toId: user.userId,
      }
      await addMessage(updatedMessage)
      await sendPushNotificationsForMessage(updatedMessage)
    }
  }
}

export const sendMessageToMark = async (msgText: string) => {
  return // temp avoid this (oct 2026)

  // const currentAppStatus = await db.appStatus.get('Tripsheets')
  // const usersDexie = await db.users.toArray()
  // if (!currentAppStatus || !usersDexie.length) return
  //
  // const mark = usersDexie.find((u) => collapseText(u.fullName) === 'markfilius')
  // if (!mark) return
  //
  // const message: Message = {
  //   messageId: nid(),
  //   createdAt: formatISO(new Date()),
  //   updatedAt: formatISO(new Date()),
  //   sentAt: formatISO(new Date()),
  //   status: 'sent',
  //   fromUserId: currentAppStatus.userId,
  //   to: 'user',
  //   toId: mark.userId,
  //   message: msgText,
  // }
  // await addMessage(message)
}
